'use client';

import { FC, useState } from 'react';
import { Megaphone } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAnnouncements } from '@/contexts/AnnouncementsProvider';
import AnnouncementsPanel from './AnnouncementsPanel';

/**
 * "What's new" bell — a megaphone button (distinct from the workflow notifications bell)
 * with an unread badge, opening the announcements panel. Mounted in multiple shells
 * (desktop topbar, mobile header, dashboard toolbar); open state is LOCAL per instance so
 * the instances don't fight over one shared popover (which made clicks appear to do nothing).
 */
const AnnouncementsBell: FC<{
  className?: string;
  variant?: 'icon' | 'menu';
  onCta?: () => void;
}> = ({ className, variant = 'icon', onCta }) => {
  const { unreadCount, markAllRead, handleCta } = useAnnouncements();
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Opening the panel clears the unread badge (mark all seen).
        if (next && unreadCount > 0) markAllRead();
      }}
    >
      <PopoverTrigger asChild>
        {variant === 'menu' ? (
          <DropdownMenuItem
            aria-label="What's new"
            className={cn(
              'cursor-pointer gap-2.5 rounded-none px-3.5 py-2.5 text-[13px] font-normal text-slate-700 focus:bg-slate-50 focus:text-slate-900',
              className,
            )}
            onSelect={(event) => event.preventDefault()}
          >
            <Megaphone className='h-[15px] w-[15px] text-slate-500' />
            <span>What's new</span>
            {unreadCount > 0 && (
              <span className='ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white'>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </DropdownMenuItem>
        ) : (
          <Button
            variant='ghost'
            size='icon'
            aria-label="What's new"
            className={cn(
              'relative h-9 w-9 rounded-lg border border-slate-200 bg-white shadow-sm hover:bg-slate-50',
              className,
            )}
          >
            <Megaphone className='h-5 w-5 text-slate-600' />
            {unreadCount > 0 && (
              <span className='absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full border-2 border-white bg-blue-600 px-1 text-[10px] font-bold text-white'>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align='end' sideOffset={8} className='w-[min(384px,calc(100vw-1.5rem))] p-0'>
        <AnnouncementsPanel
          onCta={(a) => {
            handleCta(a);
            setOpen(false);
            onCta?.();
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default AnnouncementsBell;
